import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Landing2Layout from './layout/Landing2Layout';
import PageHero2 from './components/PageHero2';
import Sym from './components/Sym';

/**
 * Página Contacto. Datos de contacto REALES (los del sitio actual, no los
 * placeholder del diseño) + formulario que postea a /api/contact con fallback a
 * mailto — misma lógica que la página vigente para no perder conversiones.
 */
const INFO = [
  { icon: 'mail', label: 'Correo', value: 'ventas@payslip.mx', href: 'mailto:ventas@payslip.mx' },
  { icon: 'chat', label: 'WhatsApp', value: '55 7243 2592', href: 'https://wa.me/525572432592' },
  { icon: 'schedule', label: 'Horario', value: 'Lun a Vie · 9:00 – 18:00' },
];

const inputStyle = {
  width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border-2)', borderRadius: 12,
  padding: '14px 16px', color: 'var(--heading)', font: "500 1rem 'Hanken Grotesk'", outline: 'none',
};
const labelStyle = { font: "600 .9rem 'Hanken Grotesk'", color: 'var(--body-strong)', marginBottom: 8, display: 'block' };

const Field = ({ label, children }) => (
  <div>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

const Contacto2 = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', message: '' });
  const [state, setState] = useState('idle'); // idle | submitting | success
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setState('submitting');
    try {
      const res = await fetch(`${import.meta.env.VITE_STRAPI_API_URL || ''}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { ...form, phone: '', role: '' } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState('success');
      setForm({ name: '', email: '', company: '', message: '' });
    } catch (err) {
      // Fallback a mailto para no perder el contacto si el endpoint no responde.
      const subject = encodeURIComponent(`Solicitud PaySlip — ${form.name || 'Contacto'}`);
      const body = encodeURIComponent(`Nombre: ${form.name}\nCorreo: ${form.email}\nEmpresa: ${form.company}\n\nMensaje:\n${form.message}\n`);
      window.location.href = `mailto:ventas@payslip.mx?subject=${subject}&body=${body}`;
      setState('idle');
    }
  };

  return (
    <Landing2Layout title="Contacto — PaySlip">
      <PageHero2
        eyebrow="Contacto"
        title="Hablemos de"
        accent="tu nómina"
        subtitle="¿Muchos colaboradores, un despacho con varias empresas o simplemente dudas? Escríbenos y te respondemos el mismo día."
        maxWidth={600}
      />

      <section style={{ maxWidth: 1080, margin: '0 auto', padding: 'clamp(28px,4vw,44px) clamp(20px,5vw,40px) clamp(56px,8vw,104px)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 20, alignItems: 'start' }}>
          {/* Columna info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {INFO.map((c) => {
              const inner = (
                <>
                  <span style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(47,107,255,.14)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                    <Sym name={c.icon} size={24} color="#6FA0FF" />
                  </span>
                  <div>
                    <div style={{ font: "600 .82rem 'Hanken Grotesk'", color: 'var(--muted-2)' }}>{c.label}</div>
                    <div style={{ font: "700 1.05rem 'Hanken Grotesk'", color: 'var(--heading)' }}>{c.value}</div>
                  </div>
                </>
              );
              const boxStyle = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 18, padding: 24, display: 'flex', gap: 15, alignItems: 'center' };
              return c.href
                ? <a key={c.label} href={c.href} target="_blank" rel="noreferrer" style={{ ...boxStyle, color: 'inherit' }}>{inner}</a>
                : <div key={c.label} style={boxStyle}>{inner}</div>;
            })}
            <div data-dark style={{ background: 'linear-gradient(160deg,#0E1A32,#00173D)', border: '1px solid rgba(111,160,255,.2)', borderRadius: 18, padding: 24 }}>
              <div style={{ font: "700 1.05rem 'Hanken Grotesk'", color: '#fff', marginBottom: 6 }}>¿Más de 5,000 colaboradores?</div>
              <p style={{ font: "400 .95rem/1.5 'Hanken Grotesk'", color: '#AEBAD2', margin: 0 }}>Te armamos un plan a la medida con SLA dedicado y un contacto asignado.</p>
            </div>
          </div>

          {/* Columna formulario / éxito */}
          {state === 'success' ? (
            <div style={{ background: 'var(--surface)', border: '1px solid rgba(111,160,255,.3)', borderRadius: 22, padding: 'clamp(28px,4vw,44px)', textAlign: 'center' }}>
              <span style={{ width: 68, height: 68, borderRadius: '50%', background: 'rgba(47,107,255,.16)', display: 'grid', placeItems: 'center', margin: '0 auto 18px' }}>
                <Sym name="mark_email_read" size={38} color="#6FA0FF" />
              </span>
              <h2 style={{ font: "600 1.7rem 'Source Serif 4'", color: 'var(--heading)', margin: '0 0 10px' }}>¡Gracias! Te contactamos pronto</h2>
              <p style={{ font: "400 1.05rem/1.55 'Hanken Grotesk'", color: 'var(--body)', margin: '0 0 26px' }}>Recibimos tu mensaje. Un miembro del equipo de PaySlip te escribirá el mismo día hábil.</p>
              <a onClick={() => navigate('/')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#2F6BFF', color: '#fff', font: "700 1rem 'IBM Plex Mono'", padding: '14px 24px', borderRadius: 12, cursor: 'pointer' }}>Volver al inicio</a>
            </div>
          ) : (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 22, padding: 'clamp(24px,3.5vw,40px)' }}>
              <form onSubmit={onSubmit}>
                <div style={{ display: 'grid', gap: 18 }}>
                  <Field label="Nombre"><input value={form.name} onChange={set('name')} required placeholder="Tu nombre" style={inputStyle} /></Field>
                  <Field label="Correo"><input type="email" value={form.email} onChange={set('email')} required placeholder="tu@empresa.mx" style={inputStyle} /></Field>
                  <Field label="Empresa"><input value={form.company} onChange={set('company')} placeholder="Nombre de tu empresa" style={inputStyle} /></Field>
                  <Field label="Mensaje"><textarea value={form.message} onChange={set('message')} required placeholder="Cuéntanos qué necesitas" rows={4} style={{ ...inputStyle, resize: 'vertical' }} /></Field>
                  <button type="submit" disabled={state === 'submitting'}
                    style={{ width: '100%', padding: 16, font: "700 1.05rem 'IBM Plex Mono'", color: '#fff', background: '#2F6BFF', border: 'none', borderRadius: 13, cursor: state === 'submitting' ? 'default' : 'pointer', boxShadow: '0 12px 30px -12px rgba(47,107,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                    {state === 'submitting'
                      ? <><span style={{ width: 19, height: 19, border: '2.5px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'ls2-spin .7s linear infinite' }} />Enviando…</>
                      : <>Enviar mensaje<Sym name="send" size={20} /></>}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </section>
    </Landing2Layout>
  );
};

export default Contacto2;
